// services
// environment
import { environment } from 'src/environments/environment';

// Third party imports
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
	MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard,
	MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalModule,
	MsalRedirectComponent, MsalService
} from '@azure/msal-angular';
import {
	BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel,
	PublicClientApplication
} from '@azure/msal-browser';

// modules
import { AppRoutingModule } from './app-routing.module';
// LOCAL
// components
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { HomeModule } from './dashboard/dashboard.module';
import { LoadingModule } from './loading/loading.module';
import { LoginModule } from './login/login.module';
import { PageNotFoundModule } from './page-not-found/page-not-found.module';
import { ProfileModule } from './profile/profile.module';
import { OptimisationComparisonsComponent } from './optimisation-comparisons/optimisation-comparisons.component';

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: environment.msal.clientId,
			authority: environment.msal.authority,
			redirectUri: environment.msal.redirectUri,
			postLogoutRedirectUri: '/',
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
		},
		system: {
			allowNativeBroker: false, // Disables WAM Broker
			loggerOptions: {
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false,
			},
		},
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	const protectedResourceMap = new Map<string, Array<string>>();

	// the access tokens for the below resources will be obtained by MSAL automatically
	protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
	protectedResourceMap.set(environment.mainApi.rootUrl, environment.mainApi.scopes);
	protectedResourceMap.set(environment.berthScheduleApi.runsUrl, environment.mainApi.scopes);
	const fastApiBase = environment.fastApi.rootUrl.replace(/\/+$/, '');
	for (const path of [
		'/inventory/optimization-context/zip-files',
		'/inventory/optimization-context/zip-files/download',
		'/inventory/optimization-context/original-inventory',
		'/inventory/optimization-context/scenarios',
		'/inventory/optimization-context/scenarios/*',
		'/inventory/optimization-context/adp',
		'/inventory/optimization-context/adp/*',
		'/inventory/optimization-context/comparison-runs',
		'/inventory/optimization-context/comparison-runs/*',
		'/lios/sales/packages',
		'/lios/sales/sources',
		'/lios/sales/comparison',
		'/model-json-feasibility',
		'/spreadsheet-snapshots',
	]) {
		protectedResourceMap.set(
			`${fastApiBase}${path}`,
			environment.mainApi.scopes
		);
	}

	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap,
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: ['user.read'],
		},
		loginFailedRoute: '/login-failed',
	};
}

@NgModule({ 
	declarations: [
		AppComponent
		],
		bootstrap: [
			AppComponent,
			MsalRedirectComponent
		], 
		imports:
		[
			BrowserModule,
			BrowserAnimationsModule,
			AppRoutingModule,
			CoreModule,
			MsalModule,
			// LOCAL
			HomeModule,
			ProfileModule,
			LoginModule,
			PageNotFoundModule,
			LoadingModule,
      OptimisationComparisonsComponent
			], 
		providers: [
			{
				provide: HTTP_INTERCEPTORS,
				useClass: MsalInterceptor,
				multi: true,
			},
			{
				provide: MSAL_INSTANCE,
				useFactory: MSALInstanceFactory,
			},
			{
				provide: MSAL_GUARD_CONFIG,
				useFactory: MSALGuardConfigFactory,
			},
			{
				provide: MSAL_INTERCEPTOR_CONFIG,
				useFactory: MSALInterceptorConfigFactory,
			},
			MsalService,
			MsalGuard,
			MsalBroadcastService,
			provideHttpClient(withInterceptorsFromDi())
			] 
		})
export class AppModule { }
